import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Clock, Download, ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { BrevetSignup } from "@/components/site/BrevetSignup";
import { getGuideBySlug, getRelatedGuides, type GuideContent } from "@/content/guides";
import { ReadingProgress } from "@/components/site/wow/ReadingProgress";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { article, breadcrumbs, person, speakable, aeoGeo } from "@/lib/seo/schema";
import { AuthorByline } from "@/components/site/AuthorByline";



export const Route = createFileRoute("/guider_/$slug")({
  loader: ({ params }): { guide: GuideContent; related: GuideContent[] } => {
    const guide = getGuideBySlug(params.slug);
    if (!guide) throw notFound();
    return { guide, related: getRelatedGuides(params.slug) };
  },
  head: ({ params, loaderData }) => {
    const guide = loaderData?.guide;
    // Titeln får ett eget tillägg så den skiljer sig från sidans H1.
    const title = guide ? `${guide.title} — ladda ner PDF | Fenny` : "Guide | Fenny";
    const description = guide?.subtitle ?? "Guide från Fenny.";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://fenny.se/guider/${params.slug}` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `https://fenny.se/guider/${params.slug}` }],
      scripts: guide
        ? [
            { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: `/guider/${params.slug}`, name: guide.title })) },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                article({
                  headline: guide.title,
                  description: guide.subtitle,
                  path: `/guider/${params.slug}`,
                  datePublished: guide.updatedAt,
                  authorName: "Fenny redaktion",
                  authorRole: "Filmstrateger",
                  section: guide.type,
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbs([
                  { name: "Hem", path: "/" },
                  { name: "Guider", path: "/guider" },
                  { name: guide.title, path: `/guider/${params.slug}` },
                ]),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                person({
                  name: "Fenny redaktion",
                  jobTitle: "Filmstrateger",
                  path: `/guider/${params.slug}`,
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                speakable(["h1", "[data-speakable]", ".tldr"]),
              ),
            },
          ]
        : [],
    };
  },
  component: GuideDetailPage,
  notFoundComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Guiden finns inte</h1>
      <p className="mt-3 text-muted-foreground">Den här guiden kan ha tagits bort eller flyttats.</p>
      <div className="mt-6">
        <Button to="/guider" variant="mint" arrow>Till alla guider</Button>
      </div>
    </div>
  ),
  errorComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Något gick fel</h1>
      <p className="mt-3 text-muted-foreground">Guiden kunde inte laddas. Försök igen.</p>
    </div>
  ),
});

function GuideDetailPage() {
  const { guide, related } = Route.useLoaderData();
  const date = new Date(guide.updatedAt).toLocaleDateString("sv-SE", { year: "numeric", month: "long" });

  return (
    <article className="pt-28">
      <ReadingProgress tone="mint" />
      <Section className="pb-6">
        <Link to="/guider" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-mint">
          <ArrowLeft className="h-4 w-4" /> Alla guider
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-mint">
          <span>{guide.eyebrow}</span>
          <span aria-hidden className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{guide.type}</span>
          <span aria-hidden className="text-muted-foreground">·</span>
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" /> {guide.readMinutes} min
          </span>
          <span aria-hidden className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">Uppdaterad {date}</span>
        </div>
        <h1 className="mt-5 text-balance text-4xl font-semibold leading-[1.05] text-butter md:text-6xl">
          {guide.title}
        </h1>
        <p className="mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl">
          {guide.subtitle}
        </p>

        {guide.pdfHref && (
          <div className="mt-8">
            <a
              href={guide.pdfHref}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
            >
              <Download className="h-4 w-4" /> Ladda ner som PDF
            </a>
          </div>
        )}
      </Section>

      <Section className="py-6">
        <div className="grid gap-12 md:grid-cols-[1fr_2.2fr]">
          {/* TOC + summary */}
          <aside className="md:sticky md:top-28 md:self-start">
            <div className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="mb-3 text-xs uppercase tracking-widest text-mint">Det här lär du dig</div>
              <ul className="space-y-2 text-sm text-foreground/85">
                {guide.summary.map((s: string) => (
                  <li key={s} className="flex gap-2">
                    <span className="text-mint">✦</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <nav className="mt-6 hidden md:block">
              <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">På sidan</div>
              <ol className="space-y-2 text-sm">
                {guide.sections.map((s: { id: string; heading: string }, i: number) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-muted-foreground transition-colors hover:text-mint">
                      {i + 1}. {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          {/* Body */}
          <div className="max-w-3xl">
            {guide.sections.map((s: { id: string; heading: string; body: string }, i: number) => (
              <section key={s.id} id={s.id} className="scroll-mt-28 border-b border-white/10 py-8 first:pt-0">
                <div className="mb-2 text-xs uppercase tracking-widest text-mint">{String(i + 1).padStart(2, "0")}</div>
                <h2 className="text-2xl font-semibold leading-tight md:text-3xl">{s.heading}</h2>
                <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground/85 md:text-lg">
                  {s.body.split("\n\n").map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </section>
            ))}
            <div className="mt-10 border-t border-white/10 pt-8">
              <AuthorByline date={guide.updatedAt} />
            </div>
          </div>
        </div>
      </Section>

      <Section className="bg-surface/40">
        <div className="mx-auto max-w-3xl">
          <BrevetSignup location={`guide_${guide.slug}`} variant="block" />
        </div>
      </Section>

      <SignatureCta
        eyebrow="Från guide till verklighet"
        title="Vill ni applicera det här hos er?"
        body="30 minuter där vi går igenom er nuvarande filmplan och pekar ut tre saker som skulle ge mest effekt nästa kvartal."
        tone="mint"
      />

      {related.length > 0 && (
        <Section>
          <div className="mb-6 text-xs uppercase tracking-widest text-mint">Läs vidare</div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {related.map((r: GuideContent) => (
              <Link
                key={r.slug}
                to="/guider/$slug"
                params={{ slug: r.slug }}
                className="group flex flex-col rounded-3xl border border-white/10 bg-surface/40 p-6 transition-all hover:border-mint/40 hover:bg-mint/[0.05]"
              >
                <div className="text-xs uppercase tracking-widest text-mint">{r.type}</div>
                <h3 className="mt-3 text-xl font-semibold leading-tight">{r.title}</h3>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{r.subtitle}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm text-mint">
                  Läs guiden <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </Section>
      )}
    </article>
  );
}
